import React, { useState } from "react";
import {
    Box,
    Typography,
    Stack,
    IconButton
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { toast } from "react-toastify";
import { getIsBusiness, getToken } from "../../Utils/authHelper";
import { postData } from "../../Webservice/Webservice";
import { useCart } from "../../context/CartContext";
import SoftCard, { PriceRow } from "../SoftCard/SoftCard";

const rubikfont = {
    fontFamily: "Rubik, sans-serif"
};

const ProductCard = ({ product, variant = "default" }) => {
    const navigate = useNavigate();
    const { fetchCartCount } = useCart();
    const [liked, setLiked] = useState(false);
    const [adding, setAdding] = useState(false);

    const handleProductClick = () => {
        navigate(`/product/${product._id || product.id}`);
        window.scrollTo(0, 0);
    };

    const goSignIn = () => navigate("/signin");
    const hasColors = Array.isArray(product.colors) && product.colors.length > 0;

    const toggleLike = async (e) => {
        e.stopPropagation();
        const token = getToken();
        if (!token) return goSignIn();
        try {
            await postData(liked ? "wishlist/remove" : "wishlist/add", { product_id: product._id || product.id }, `Bearer ${token}`, false, false);
            setLiked(!liked);
        } catch (err) {
            toast.error("Could not update your wishlist. Please try again.");
        }
    };

    const quickAdd = async () => {
        const token = getToken();
        if (!token) return goSignIn();
        // a product sold in several colours needs the colour chosen first
        if (hasColors) return handleProductClick();
        setAdding(true);
        try {
            const res = await postData("cart/add-to-cart", { product_id: product._id || product.id, quantity: 1 }, `Bearer ${token}`, false, false);
            if (res && (res.success || res.cart)) {
                toast.success("Added to cart");
                fetchCartCount();
            } else {
                toast.error(res?.message || "Could not add to cart");
            }
        } catch (err) {
            toast.error(err.message || "Could not add to cart");
        } finally {
            setAdding(false);
        }
    };

    const isBusiness = getIsBusiness();
    const mrp = isBusiness
        ? (product.b2b_mrp || product.b2c_mrp || product.maximumRetailPrice || 0)
        : (product.b2c_mrp || product.maximumRetailPrice || (product.mrp && (typeof product.mrp === 'string' ? parseFloat(product.mrp.replace(/[^\d.]/g, '')) : product.mrp)) || 0);
    const price = product.finalPrice || (isBusiness ? (product.b2b_sellingPrice || product.b2c_sellingPrice) : (product.b2c_sellingPrice || product.sellingPriceNum)) || 0;
    const discountPercentage = mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;

    // LISTING VARIANT (Text below image, full image visible)
    if (variant === "listing") {
        return (
            <Box
                onClick={handleProductClick}
                        sx={{
                    width: "100%",
                    borderRadius: "16px",
                    overflow: "hidden",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    bgcolor: "rgba(255,255,255,0.88)",
                    border: "1px solid rgba(200,160,84,0.15)",
                    boxShadow: "0 2px 12px rgba(200,160,84,0.06)",
                    "&:hover": {
                        transform: "translateY(-5px)",
                        boxShadow: "0 12px 32px rgba(200,160,84,0.14)",
                        border: "1px solid rgba(200,160,84,0.3)",
                    }
                }}
            >
                {/* Image Container - Fully Visible Product */}
                <Box
                    sx={{
                        position: "relative",
                        width: "100%",
                        aspectRatio: "1/1.1",
                        overflow: "hidden",
                        bgcolor: "#f9f9f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 2
                    }}
                >
                    <Box
                        component="img"
                        src={product.product_images?.[0] || product.image?.[0] || product.img || "https://via.placeholder.com/400x500"}
                        alt={product.title || product.name || "Product"}
                        sx={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                            transition: "transform 0.5s ease"
                        }}
                    />

                    {/* Badge */}
                    {discountPercentage > 0 && product.inStock !== false && (
                        <Box sx={{ position: "absolute", top: 12, left: 12, zIndex: 3 }}>
                            <Box sx={{ bgcolor: "#C8A054", color: "white", px: 1, py: 0.3, borderRadius: "4px", fontSize: "10px", fontWeight: 600, ...rubikfont }}>
                                {discountPercentage}% OFF
                            </Box>
                        </Box>
                    )}

                    {product.inStock === false && (
                        <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(255,255,255,0.6)", zIndex: 5, display: "flex", alignItems: "center", justifyContent: "center", p: 1 }}>
                            <Box sx={{ bgcolor: "rgba(177, 39, 4, 0.9)", color: "white", px: 1.5, py: 0.8, borderRadius: "4px", fontSize: "11px", fontWeight: 700, textAlign: "center", ...rubikfont, letterSpacing: "0.5px" }}>
                                TEMPORARILY OUT OF STOCK
                            </Box>
                        </Box>
                    )}
                </Box>

                {/* Separated Content Section - Below Image */}
                <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 600, letterSpacing: "1px", color: "#C8A054", mb: 0.5, textTransform: "uppercase", ...rubikfont }}>
                        {product.brand || "ECOM EXCLUSIVE"}
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: "15px",
                            fontWeight: 500,
                            lineHeight: 1.4,
                            color: "#1a1a1a",
                            mb: 1.5,
                            height: "2.8em",
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            ...rubikfont
                        }}
                    >
                        {product.title || product.name}
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="center" sx={{ flexWrap: "wrap" }}>
                        <Typography sx={{ fontSize: { xs: "16px", sm: "18px" }, fontWeight: 700, color: "#1a1a1a", ...rubikfont }}>
                            {`₹${Number(price || 0).toFixed(2)}`}
                        </Typography>
                        {mrp > price && (
                            <Typography sx={{ fontSize: { xs: "11px", sm: "12px" }, color: "#999", textDecoration: "line-through", fontWeight: 500, ...rubikfont }}>
                                {`₹${Number(mrp || 0).toFixed(2)}`}
                            </Typography>
                        )}
                    </Stack>

                    <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #f0f0f0", display: "flex", justifyContent: "center" }}>
                        <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#C8A054", textTransform: "uppercase", letterSpacing: "0.5px", display: "flex", alignItems: "center", gap: 1 }}>
                            <ShoppingCartOutlinedIcon sx={{ fontSize: 14 }} />
                            View Details
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    }

    // DEFAULT VARIANT: soft rounded card for the home page
    const image = product.product_images?.[0] || product.image?.[0] || product.img || "https://via.placeholder.com/400x500";
    const outOfStock = product.inStock === false;
    return (
        <SoftCard
            onClick={handleProductClick}
            outOfStock={outOfStock}
            badge={discountPercentage > 0 ? `${discountPercentage}% OFF` : null}
            image={<img className="soft-img" src={image} alt={product.title || product.name || "Product"} />}
            topRight={
                <IconButton
                    size="small"
                    aria-label={liked ? "Remove from wishlist" : "Add to wishlist"}
                    onClick={toggleLike}
                    sx={{ width: 34, height: 34, bgcolor: "rgba(255,255,255,0.94)", color: liked ? "#e53935" : "#6b6b6b", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", "&:hover": { bgcolor: "#fff", color: "#e53935" } }}
                >
                    {liked ? <FavoriteIcon sx={{ fontSize: 19 }} /> : <FavoriteBorderIcon sx={{ fontSize: 19 }} />}
                </IconButton>
            }
            action={{ icon: <ShoppingCartOutlinedIcon sx={{ fontSize: 19 }} />, label: hasColors ? "Select options" : "Add to cart", onClick: quickAdd, loading: adding }}
            kicker={product.brand}
            title={product.title || product.name}
        >
            <PriceRow
                price={`₹${Number(price || 0).toFixed(0)}`}
                mrp={mrp > price ? `₹${Number(mrp || 0).toFixed(0)}` : null}
                note={discountPercentage > 0 ? `${discountPercentage}% off` : null}
            />
        </SoftCard>
    );
};

export default ProductCard;
